import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, Archive, AlertCircle, CalendarPlus, BadgeCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useMyEvents from '../hooks/useMyEvents';
import NavigationBar from '../components/NavigationBar';
import { formatEventDates, formatEventLocation } from '../components/public/PublicShell';
import {
    eventHomePath,
    eventRegistrationPath,
    isEventPast,
    describeRegistration,
    isRegistered,
    canRegister,
} from '../config/events';
import './EventsList.css';

const DATE_BLOCK_OPTS = (tz) => ({ timeZone: tz || undefined });

const DateBlock = ({ event }) => {
    if (!event?.StartDate) return null;
    const start = new Date(event.StartDate);
    const opts = DATE_BLOCK_OPTS(event.TimeZone);
    return (
        <div className="evl-date" aria-hidden="true">
            <span className="evl-date-month">{start.toLocaleDateString('en-US', { ...opts, month: 'short' })}</span>
            <span className="evl-date-day">{start.toLocaleDateString('en-US', { ...opts, day: 'numeric' })}</span>
            <span className="evl-date-year">{start.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}</span>
        </div>
    );
};

const EventCard = ({ event, userEmail }) => {
    const navigate = useNavigate();
    const past = isEventPast(event);
    const registered = isRegistered(event.registration);
    const status = describeRegistration(event.registration);
    const showRegister = canRegister(event);
    const registrationStarted = event.registration?.status === 'registering';
    const dates = formatEventDates(event);
    const location = formatEventLocation(event);
    const homePath = eventHomePath(event.EventNameId);
    const registerPath = `${eventRegistrationPath(event.EventNameId)}${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`;

    const open = () => navigate(homePath);

    return (
        <article className={`evl-card ${past ? 'is-past' : ''}`}>
            <DateBlock event={event} />

            <div className="evl-card-body">
                <div className="evl-card-top">
                    <h3 className="evl-card-title">
                        <Link to={homePath}>{event.Name}</Link>
                    </h3>
                    <div className="evl-pills">
                        {past ? (
                            <span className="evl-pill tone-gray"><Archive size={12} aria-hidden="true" /> Archived</span>
                        ) : (
                            <span className={`evl-pill tone-${status.tone}`}>
                                {registered && <BadgeCheck size={12} aria-hidden="true" />}
                                {status.label}
                            </span>
                        )}
                    </div>
                </div>

                <ul className="evl-meta">
                    {dates && <li><Calendar size={14} aria-hidden="true" /><span>{dates}</span></li>}
                    {location && <li><MapPin size={14} aria-hidden="true" /><span>{location}</span></li>}
                </ul>

                {registered && event.registration?.event_entity_name && (
                    <p className="evl-note">Attending as <strong>{event.registration.event_entity_name}</strong></p>
                )}
                {!past && !registered && event.registration?.status === 'in_review' && (
                    <p className="evl-note">We're reviewing your registration. You'll get an email once it's confirmed.</p>
                )}
                {!past && showRegister && !registrationStarted && (
                    <p className="evl-note">You're not registered for this event yet.</p>
                )}
                {!past && registrationStarted && (
                    <p className="evl-note">You started registering but didn't finish. Pick up where you left off.</p>
                )}
            </div>

            <div className="evl-card-actions">
                {showRegister && (
                    <Link to={registerPath} className="evl-btn primary">
                        <CalendarPlus size={16} aria-hidden="true" />
                        {registrationStarted ? 'Continue registration' : 'Register'}
                    </Link>
                )}
                <button type="button" className={`evl-btn ${showRegister ? 'secondary' : 'primary'}`} onClick={open}>
                    {past ? 'View archive' : 'Open event'}
                    <ArrowRight size={16} aria-hidden="true" />
                </button>
            </div>
        </article>
    );
};

const EventsList = () => {
    const { user } = useAuth();
    const { events, loading, error: loadError } = useMyEvents();
    const error = loadError ? 'We couldn’t load your events. Please try again.' : '';

    const upcoming = events.filter((e) => !isEventPast(e));
    const archived = events.filter((e) => isEventPast(e));

    return (
        <>
            <NavigationBar />
            <main className="evl-page">
                <div className="evl-inner">
                    <header className="evl-head">
                        <div className="evl-eyebrow">Funds4Charity</div>
                        <h1 className="evl-title">Your events</h1>
                        <p className="evl-subtitle">Open an event you're attending, or register for an upcoming one. Past events stay available as read-only archives.</p>
                    </header>

                    {loading && (
                        <div className="evl-status" role="status">
                            <span className="pshell-spinner" aria-hidden="true" /> Loading your events…
                        </div>
                    )}

                    {!loading && error && (
                        <div className="pshell-alert error" role="alert">
                            <AlertCircle size={16} aria-hidden="true" />
                            <div>{error}</div>
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            <section className="evl-section" aria-labelledby="evl-upcoming">
                                <div className="evl-section-head">
                                    <h2 id="evl-upcoming">Upcoming</h2>
                                    <span className="evl-count">{upcoming.length}</span>
                                </div>
                                {upcoming.length === 0 ? (
                                    <div className="evl-empty">No upcoming events right now. Check back soon.</div>
                                ) : (
                                    <div className="evl-grid">
                                        {upcoming.map((event) => (
                                            <EventCard key={event.EventId} event={event} userEmail={user?.email} />
                                        ))}
                                    </div>
                                )}
                            </section>

                            {archived.length > 0 && (
                                <section className="evl-section" aria-labelledby="evl-archived">
                                    <div className="evl-section-head">
                                        <h2 id="evl-archived">Archived</h2>
                                        <span className="evl-count">{archived.length}</span>
                                    </div>
                                    <div className="evl-grid">
                                        {archived.map((event) => (
                                            <EventCard key={event.EventId} event={event} userEmail={user?.email} />
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </div>
            </main>
        </>
    );
};

export default EventsList;
